import { motion, AnimatePresence, Variants } from "framer-motion";
import { CategoryType } from "../../clothing.types";
import transparentBg from "../../assets/images/category-transparent-bg.png";
import activeBg from "../../assets/images/category-active-bg.png";

import useStore from "../../hooks/use-store";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import clickSound from "../../../../assets/audio/click.wav";

interface Props {
  className?: string;
  categories: CategoryType[];
}

const classesBySize: Record<CategoryType["size"], string> = {
  sm: "w-[110px] h-[110px]",
  xl: "w-[230px] h-[180px]",
  tall: "w-[110px] h-[180px]",
  wide: "w-[230px] h-[110px]",
};

// ─── Variants ────────────────────────────────────────────────────────────────

/**
 * Container: propagates stagger to every card child.
 * staggerChildren fires sequentially without needing custom delay per-item.
 */
const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
};

/**
 * Card entry: blur + scale + y gives a premium "materialise" feel.
 * easeOutExpo [0.22, 1, 0.36, 1] feels instant at the start, then
 * settles gracefully — much better than a plain ease-out.
 */
const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.82,
    y: 18,
    // Removed filter:blur — CSS blur forces CPU rasterization every frame in CEF
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/**
 * Floating icon: infinite sinusoidal y movement while active.
 * "idle" snaps back to rest with a short ease-out.
 */
const floatVariants: Variants = {
  idle: {
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
  floating: {
    y: [0, -5, 0],
    transition: {
      duration: 2.8,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    },
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

const ClothingCategories = ({ className, categories }: Props) => {
  const { activeCategory, setActiveCategory } = useStore();
  const { playEffect } = useAudioEffects();

  return (
    <motion.div
      className={`flex flex-wrap gap-3 py-2 ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {categories?.map((category) => {
        const isActive = category.id === activeCategory?.id;

        return (
          <motion.div
            key={category.id}
            className={`relative overflow-hidden cursor-pointer select-none ${classesBySize[category.size]}`}
            // ── Stagger entry from container ──
            variants={cardVariants}
            // ── Spring hover: elastic lift ──
            whileHover={{
              scale: 1.005,
              y: -6,
              transition: {
                type: "spring",
                stiffness: 360,
                damping: 20,
              },
            }}
            // ── Spring tap: tactile squish ──
            whileTap={{
              scale: 0.95,
              transition: {
                type: "spring",
                stiffness: 500,
                damping: 28,
              },
            }}
            onClick={() => {
              playEffect(clickSound);
              setActiveCategory(category);
            }}
          >
            {/* ── Layer 1: Always-visible base background ── */}
            <img
              src={transparentBg}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute inset-0 w-full h-full object-fill pointer-events-none select-none"
            />

            {/* ── Layer 2: Active background — crossfades in/out ──
                mode="sync" lets both images be visible simultaneously
                during the transition, producing a true crossfade.        */}
            <AnimatePresence mode="sync">
              {isActive && (
                <motion.img
                  key="active-bg"
                  src={activeBg}
                  alt=""
                  aria-hidden
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-fill pointer-events-none select-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.38, ease: "easeInOut" }}
                />
              )}
            </AnimatePresence>

            {/* ── Layer 4: Hover sheen (diagonal gloss) ── */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.22 }}
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 52%, rgba(255,255,255,0.025) 100%)",
              }}
            />

            {/* ── Content ── */}
            <div className="relative z-10 flex flex-col justify-between h-full items-center p-2">
              <div />

              {/*
               * Wrapper handles the continuous float (y keyframe loop).
               * Inner motion.img handles scale + filter independently —
               * splitting concerns keeps each animation clean.
               */}
              <motion.div
                className="w-full h-full flex items-center justify-center"
                variants={floatVariants}
                //   animate={isActive ? "floating" : "idle"}
                style={{ willChange: "transform" }}
              >
                <motion.img
                  src={category.iconUrl}
                  alt={`${category.name}'s icon`}
                  loading="lazy"
                  width="100%"
                  height="100%"
                  className={`w-full h-full object-contain max-h-[120px] ${category.iconClass}`}
                  // Removed filter from animate — animating drop-shadow via JS runs
                  // CPU repaint every frame in CEF. CSS transition handles it natively
                  // at a much lower cost (browser defers to its own paint scheduler).
                  style={{
                    filter: isActive
                      ? "drop-shadow(-5px 7px 12px rgba(255,255,255,0.44)) drop-shadow(0px 0px 10px rgba(255,255,255,0.18))"
                      : "drop-shadow(-5px 7px 6px rgba(255,255,255,0.25))",
                    transition: "filter 0.35s ease-out",
                  }}
                  animate={{
                    scale: isActive ? 1.1 : 1,
                  }}
                  transition={{
                    scale: { type: "spring", stiffness: 300, damping: 18 },
                  }}
                />
              </motion.div>

              {/* Label: dims when inactive, brightens + lifts when active */}
              <motion.p
                className="gilroy-font text-[0.8rem] text-center mt-2"
                animate={{
                  opacity: isActive ? 1 : 0.6,
                  y: isActive ? 0 : 1,
                  color: isActive
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(255, 255, 255, 0.8)",
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {category.name}
              </motion.p>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default ClothingCategories;
